import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import React, { FC } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useAthlete } from 'src/common/hooks/useAthlete'
import { useUser } from 'src/common/hooks/useUser'
import { NavAvatar } from './NavAvatar'
import { NavHeader } from './NavHeader'
import { NavItem } from './NavItem'
import { NavUserMenu } from './NavUserMenu'
import { Offer } from './Offer'

export type NavLink = {
    label: string
    faicon: string | null
    href: string | null
    isHeader: boolean
    badgeNumber?: number
}

export type NavigationPanelProps = {
    isLoggedIn: boolean
    navlinks: Array<NavLink>
    openNavPanel: () => void
    closeNavPanel: () => void
    isNavigationPanelOpen: boolean
    isSessionLoading?: boolean
}

const UserContentBox = ({ children }) => (
    <div className="flex flex-row items-center bg-white pt-3 pb-3 pl-2 xs:pl-6 xs:mb-3 mb-1 w-full relative pr-2">
        {children}
    </div>
)

export const NavigationPanel: FC<NavigationPanelProps> = ({
    navlinks = [],
    // openNavPanel,
    closeNavPanel,
    isNavigationPanelOpen,
    isSessionLoading = false,
    isLoggedIn
}) => {
    const router = useRouter()
    const NavPanelVariants = {
        hidden: { left: '-90%' }, // max-w-sm
        visible: { left: '0px' }
    }
    const user = useUser()
    const athlete = useAthlete()

    const NavBody = () => {
        return (
            <>
                <UserContentBox>
                    {user !== null && isLoggedIn ? (
                        <React.Fragment>
                            <NavAvatar imageUrl={athlete?.user?.imgProfileUrl} />

                            <div className="xs:ml-3 mr-auto min-w-[100px]">
                                <p className="text-sm xs:text-lg md:leading-6 line-clamp-1">
                                    {athlete?.user?.fullName}
                                </p>
                                <p className="text-xs xs:text-sm mr-2 text-gray-600 line-clamp-1">
                                    {user.email}
                                </p>
                                <p className="text-xs xs:text-sm text-yf-purple">
                                    {user.isAdmin || user.isSuperAdmin ? 'Admin' : 'User'}
                                </p>
                            </div>

                            <div className="md:mr-9">
                                <NavUserMenu />
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div>
                                <NavAvatar imageUrl={null} />
                            </div>
                            <div className="ml-2">
                                <p className="mb-2 text-xs font-normal">
                                    {isSessionLoading ? (
                                        <Skeleton width={213} height={13} />
                                    ) : (
                                        'Looks like you are not logged in.'
                                    )}
                                </p>
                                <div className="flex">
                                    <button
                                        onClick={() => router.push('/login')}
                                        className="py-2 text-xs text-yf-purple border border-yf-purple rounded w-1/2 mr-2 min-w-[100px] hover:bg-yf-purple hover:text-white transition-all cursor-pointer"
                                    >
                                        Sign In
                                    </button>
                                    <button
                                        onClick={() => router.push('/register')}
                                        className="py-2 text-xs text-white bg-yf-green rounded w-2/3 min-w-[110px] hover:bg-white border border-yf-green hover:text-yf-green transition-all cursor-pointer"
                                    >
                                        Join for Free
                                    </button>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </UserContentBox>

                <div className="flex justify-center mb-1 xs:px-6 px-1">
                    {user && user.stripeSubscriptionId !== null ? (
                        <Offer variant="referral" />
                    ) : (
                        <div className="flex-column space-y-3">
                            <Offer variant="1-month-free" />
                            <Offer variant="referral" />
                        </div>
                    )}
                </div>

                {/* <div className="xs:px-6 px-2 xs:mb-4 mb-1 relative">
                    <input
                        placeholder="Search..."
                        className="mt-1 block w-full rounded-xl border-solid border xs:p-5 p-2 border-gray-300 focus:border-yf-purple-500 focus:bg-white focus:ring-0 focus:outline-none transition-colors"
                    />
                </div> */}

                <ul className="bg-white pt-2 pb-12">
                    {navlinks?.map((item, idx) => {
                        if (item.isHeader) {
                            return <NavHeader key={idx} label={item.label} />
                        }

                        return (
                            <NavItem
                                closeNav={closeNavPanel}
                                key={idx}
                                label={item.label}
                                faicon={item.faicon ? item.faicon : undefined}
                                href={item.href ? item.href : ''}
                                badgeNumber={item.badgeNumber ? item.badgeNumber : null}
                            />
                        )
                    })}
                </ul>
            </>
        )
    }

    return (
        <div>
            <motion.nav
                variants={NavPanelVariants}
                animate={isNavigationPanelOpen ? 'visible' : 'hidden'}
                className={`flex z-[100] bg-white w-full h-full max-w-[90%] md:max-w-[344px] pt-1 flex-col top-0 left-0 absolute min-h-screen`}
                initial={'hidden'}
            >
                <NavBody />
            </motion.nav>
        </div>
    )
}
